import { useCallback, useMemo } from 'react';

import { defaultStyles } from '@aline/common';
import { values } from 'ramda';
import type { DeclarativeMenuConfig } from 'src/components/declarative-menu';
import { DECLARATIVE_MENU_SEPARATOR } from 'src/components/declarative-menu';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { useCurrentUserId } from 'src/gql';
import type { Hotkey } from 'src/helpers/hotkeys';
import { useAuthorizedTransforms } from 'src/hooks';
import { editorHotkeys, useEditor } from 'src/layout-engine';
import { useCurrentEditingMode } from 'src/store/document/selectors';

import { plannedOnly, useIfEditableMixin, useIfLoggedInMixin } from './menu-mixins';

export function useParagraphStylesMenu() {
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const currentUserId = useCurrentUserId();
  const editingMode = useCurrentEditingMode();
  const editor = useEditor();
  const ifEditable = useIfEditableMixin();
  const ifLoggedIn = useIfLoggedInMixin();
  const activeStyleVariant = useEditorTabStateSelector(
    state => state.derived.paragraphProperties.styleVariant
  );

  const applyStyle = useCallback(
    (variant: string) => () => {
      if (!currentUserId || !editingMode) return;
      AuthorizedTransforms.setParagraphProps(editor, editingMode, currentUserId, { variant });
    },
    [AuthorizedTransforms, currentUserId, editingMode, editor]
  );

  const updateStyle = useCallback(
    (styleVariant: string) => () => {
      if (!currentUserId || !editingMode) return;
      AuthorizedTransforms.updateStylesToMatch(editor, {
        authorId: currentUserId,
        editingMode,
        styleVariant,
      });
    },
    [AuthorizedTransforms, currentUserId, editingMode, editor]
  );

  return useMemo(
    (): DeclarativeMenuConfig => [
      ...values(defaultStyles).map(({ fontStyle, styleVariant }) => ({
        checked: styleVariant === activeStyleVariant,
        label: fontStyle,
        onClick: applyStyle(styleVariant),
        submenu: [
          {
            hint: getStyleHotkey(styleVariant)?.hint,
            label: `Apply '${fontStyle}'`,
            onClick: applyStyle(styleVariant),
            ...ifEditable,
          },
          {
            label: `Update '${fontStyle}' to match`,
            onClick: updateStyle(styleVariant),
            ...ifEditable,
          },
        ],
      })),
      DECLARATIVE_MENU_SEPARATOR,
      {
        label: 'Options',
        submenu: [
          { label: 'Save as my default styles', ...ifLoggedIn },
          { label: 'Use my default styles', ...ifEditable },
          { label: 'Reset styles', ...ifEditable },
        ],
        ...plannedOnly,
      },
    ],
    [activeStyleVariant, applyStyle, ifEditable, ifLoggedIn, updateStyle]
  );
}

function getStyleHotkey(styleVariant: string): Hotkey<never> | null {
  switch (styleVariant) {
    case 'style-heading1':
      return editorHotkeys.styleHeading1;
    case 'style-heading2':
      return editorHotkeys.styleHeading2;
    case 'style-heading3':
      return editorHotkeys.styleHeading3;
    case 'style-normal':
      return editorHotkeys.styleNormal;
    default:
      return null;
  }
}
